import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nProvider";
import { toast } from "sonner";
import { Plus, Folder, MapPin, ArrowLeft, Trash2, ExternalLink } from "lucide-react";

export function ProjectsList() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ namn: "", plats: "", ansvarig: "", beskrivning: "" });

  const load = async () => { const { data } = await api.get("/projects"); setItems(data || []); };
  useEffect(() => { if (user) load(); }, [user]);

  if (!user) return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("add.loginRequired")}</div></Layout>;

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/projects", form);
      toast.success(t("prj.created"));
      setShowForm(false);
      setForm({ namn: "", plats: "", ansvarig: "", beskrivning: "" });
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || t("dash.failed")); }
  };

  return (
    <Layout>
      <div className="max-w-[1440px] mx-auto px-6 pt-10 pb-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="label-tech">{t("prj.label")}</div>
            <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tighter mt-2">{t("prj.title")}</h1>
          </div>
          <button onClick={() => setShowForm(!showForm)} className="btn-primary" data-testid="prj-new-btn"><Plus size={14} /> {t("prj.new")}</button>
        </div>

        {showForm && (
          <form onSubmit={create} className="mt-6 border border-[var(--ink-200)] bg-[var(--bone)] p-5 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="prj-form">
            <div><div className="label-tech mb-2">{t("prj.namn")}</div><input className="input-tech" value={form.namn} onChange={(e) => setForm({ ...form, namn: e.target.value })} required data-testid="prj-namn-input" /></div>
            <div><div className="label-tech mb-2">{t("prj.plats")}</div><input className="input-tech" value={form.plats} onChange={(e) => setForm({ ...form, plats: e.target.value })} data-testid="prj-plats-input" /></div>
            <div><div className="label-tech mb-2">{t("prj.ansvarig")}</div><input className="input-tech" value={form.ansvarig} onChange={(e) => setForm({ ...form, ansvarig: e.target.value })} data-testid="prj-ansvarig-input" /></div>
            <div className="md:col-span-2"><div className="label-tech mb-2">{t("prj.beskrivning")}</div><textarea rows={3} className="input-tech" value={form.beskrivning} onChange={(e) => setForm({ ...form, beskrivning: e.target.value })} data-testid="prj-beskrivning-input" /></div>
            <div className="md:col-span-2 flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setShowForm(false)}>{t("admin.orgs.cancel")}</button>
              <button type="submit" className="btn-primary" data-testid="prj-submit-btn">{t("admin.orgs.save")}</button>
            </div>
          </form>
        )}

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--ink-200)] border border-[var(--ink-200)]">
          {items.length === 0 ? (
            <div className="bg-[var(--surface)] p-10 label-tech md:col-span-2 lg:col-span-3">{t("prj.empty")}</div>
          ) : items.map((p) => (
            <Link key={p.id} to={`/projects/${p.id}`} className="bg-[var(--surface)] p-5 hover:bg-[var(--surface-alt)] transition-colors block" data-testid={`prj-card-${p.id}`}>
              <div className="flex items-center gap-2 label-tech"><Folder size={12} /> {p.kopplade?.length || 0} kopplade</div>
              <div className="font-display text-lg mt-2">{p.namn}</div>
              <div className="mt-2 text-sm text-[var(--ink-700)] inline-flex items-center gap-1"><MapPin size={12} /> {p.plats || "—"}</div>
              {p.ansvarig && <div className="label-tech mt-1">{p.ansvarig}</div>}
            </Link>
          ))}
        </div>
      </div>
    </Layout>
  );
}

export function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [linkedDetails, setLinkedDetails] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
      // Resolve linked objects' names by fetching each (best-effort)
      const details = await Promise.all((data.kopplade || []).map(async (k) => {
        try {
          const url = { machine: `/machines/${k.object_id}`, inventory: `/inventories/${k.object_id}`, material: `/listings/${k.object_id}` }[k.object_type];
          const { data: o } = await api.get(url);
          return { ...k, name: o.namn || o.title, link: { machine: `/machines/${k.object_id}`, inventory: `/inventory/${k.object_id}`, material: `/listings/${k.object_id}` }[k.object_type] };
        } catch { return { ...k, name: "(borttagen)", link: null }; }
      }));
      setLinkedDetails(details);
    } catch { toast.error(t("detail.notFound")); navigate("/projects"); }
  }, [id, navigate, t]);

  useEffect(() => { if (user) load(); }, [user, load]);

  if (!user) return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("add.loginRequired")}</div></Layout>;
  if (!project) return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("detail.loading")}</div></Layout>;

  const isOwner = project.owner_id === user.user_id || user.role === "admin";

  const del = async () => {
    if (!confirm(t("dash.confirmDelete"))) return;
    try { await api.delete(`/projects/${id}`); toast.success(t("prj.deleted")); navigate("/projects"); }
    catch (err) { toast.error(err.response?.data?.detail || t("dash.failed")); }
  };

  const detach = async (entry) => {
    try {
      await api.post(`/projects/${id}/detach`, { object_type: entry.object_type, object_id: entry.object_id });
      toast.success(t("prj.detach"));
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || t("dash.failed")); }
  };

  return (
    <Layout>
      <div className="max-w-[1100px] mx-auto px-6 pt-8 pb-16">
        <Link to="/projects" className="label-tech hover:text-[var(--ink)] inline-flex items-center gap-1" data-testid="prj-back-link">
          <ArrowLeft size={12} /> {t("nav.backToList")}
        </Link>

        <div className="mt-4 grid grid-cols-12 gap-px bg-[var(--ink-200)] border border-[var(--ink-200)]">
          <div className="col-span-12 lg:col-span-7 bg-[var(--bone)] p-6">
            <div className="label-tech inline-flex items-center gap-2"><Folder size={12} /> {t("prj.title")}</div>
            <h1 className="font-display text-3xl tracking-tight mt-2">{project.namn}</h1>
            <div className="mt-3 text-sm text-[var(--ink-700)] inline-flex items-center gap-1"><MapPin size={12} /> {project.plats || "—"}</div>
            {project.ansvarig && <div className="label-tech mt-2">{t("prj.ansvarig")}: {project.ansvarig}</div>}
            {project.beskrivning && <p className="mt-4 text-[var(--ink-700)] whitespace-pre-line">{project.beskrivning}</p>}
            {isOwner && (
              <button className="btn-ghost text-[var(--brick)] mt-4" onClick={del} data-testid="prj-delete-btn"><Trash2 size={12} /> {t("prj.deleted")}</button>
            )}
          </div>

          <div className="col-span-12 lg:col-span-5 bg-[var(--surface)] p-6">
            <div className="label-tech">{t("prj.linked")}</div>
            <div className="mt-3 divide-y divide-[var(--ink-200)]">
              {linkedDetails.length === 0 && <div className="label-tech py-3">{t("prj.linkedNone")}</div>}
              {linkedDetails.map((k) => (
                <div key={`${k.object_type}-${k.object_id}`} className="py-3 flex items-center justify-between gap-2" data-testid={`linked-${k.object_id}`}>
                  <div className="min-w-0">
                    <div className="label-tech">{k.object_type}</div>
                    <div className="font-display truncate">{k.name}</div>
                  </div>
                  <div className="flex gap-2">
                    {k.link && <Link to={k.link} className="btn-ghost"><ExternalLink size={12} /></Link>}
                    {isOwner && <button onClick={() => detach(k)} className="btn-ghost text-[var(--brick)]" data-testid={`detach-${k.object_id}`}>×</button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
